import { ArrowSyncCircle20Regular, CloudSync20Regular, Eraser20Regular, New20Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { EnvironmentProvisioningState } from '../../../constants/environment';
import {
    MetadataItemViewModel,
    ShimmeredMetadataItemViewModel,
    ShimmeredMetadataItemWidth,
} from '../../common/metadata/models';
import { EnvironmentExpirationDateOffset, EnvironmentExpirationDateOffsetUnit } from '../models';

interface EnvironmentTypeMetadataProps {
    environmentType: string;
}

interface ExpirationDateOffsetMetadataProps {
    expirationDateOffset: EnvironmentExpirationDateOffset;
}

interface StateMetadataViewModel extends Omit<MetadataItemViewModel, 'description'> {
    description: MessageDescriptor;
}

const messages = defineMessages({
    creatingAriaLabel: {
        id: 'EnvironmentStateMessage_Creating_AriaLabel',
        defaultMessage: 'Creating your environment',
        description: 'Aria label for tracking progress of environment creation in environment card',
    },
    deletingAriaLabel: {
        id: 'EnvironmentStateMessage_Deleting_AriaLabel',
        defaultMessage: 'Deleting your environment',
        description: 'Aria label for tracking progress of environment deletion in environment card',
    },
    deployingAriaLabel: {
        id: 'EnvironmentStateMessage_Deploying_AriaLabel',
        defaultMessage: 'Deploying your environment',
        description: 'Aria label for tracking progress of environment deployment in environment card',
    },
    updatingAriaLabel: {
        id: 'EnvironmentStateMessage_Updating_AriaLabel',
        defaultMessage: 'Updating your environment',
        description: 'Aria label for tracking progress of environment updating in environment card',
    },
});

export const EnvironmentTypeMetadata: React.FC<EnvironmentTypeMetadataProps> = (
    props: EnvironmentTypeMetadataProps
) => {
    const { environmentType } = props;

    // Memoized data
    const values = React.useMemo(() => ({ environmentType }), [environmentType]);

    return (
        <FormattedMessage
            id="EnvironmentTypeMetadata_Metadata_Text"
            defaultMessage="{environmentType}"
            description="Text displaying environment type. {environmentType} should not be localized."
            values={values}
        />
    );
};

export const ExpirationDateOffsetMetadata: React.FC<ExpirationDateOffsetMetadataProps> = (
    props: ExpirationDateOffsetMetadataProps
) => {
    const { expirationDateOffset } = props;

    const { offset, unit } = expirationDateOffset;

    const { formatNumber } = useIntl();

    // Memoized data
    const offsetInteger = React.useMemo(() => Math.floor(offset), [offset]);

    const values = React.useMemo(
        () => ({
            offset: formatNumber(offsetInteger),
        }),
        [offsetInteger, formatNumber]
    );

    const environmentExpirationDisplay = React.useMemo(() => {
        switch (unit) {
            case EnvironmentExpirationDateOffsetUnit.Years:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Year"
                            defaultMessage="Expires in 1 year"
                            description="Text displaying that the environment will expire in 1 year."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Years"
                            defaultMessage="Expires in {offset} years"
                            description="Text displaying how many years from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Months:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Month"
                            defaultMessage="Expires in 1 month"
                            description="Text displaying that the environment will expire in 1 month."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Months"
                            defaultMessage="Expires in {offset} months"
                            description="Text displaying how many months from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Weeks:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Week"
                            defaultMessage="Expires in 1 week"
                            description="Text displaying that the environment will expire in 1 week."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Weeks"
                            defaultMessage="Expires in {offset} weeks"
                            description="Text displaying how many weeks from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Days:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Day"
                            defaultMessage="Expires in 1 day"
                            description="Text displaying that the environment will expire in 1 day."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Days"
                            defaultMessage="Expires in {offset} days"
                            description="Text displaying how many days from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Hours:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Hour"
                            defaultMessage="Expires in 1 hour"
                            description="Text displaying the environment will expire in 1 hour."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Hours"
                            defaultMessage="Expires in {offset} hours"
                            description="Text displaying how many hours from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Minutes:
                if (offsetInteger === 1) {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Minute"
                            defaultMessage="Expires in 1 minute"
                            description="Text displaying that the environment will expire in 1 minute."
                        />
                    );
                } else {
                    return (
                        <FormattedMessage
                            id="ExpirationDateOffsetMetadata_Metadata_Minutes"
                            defaultMessage="Expires in {offset} minutes"
                            description="Text displaying how many minutes from now the environment will expire. {offset} should not be localized."
                            values={values}
                        />
                    );
                }
            case EnvironmentExpirationDateOffsetUnit.Seconds:
                return (
                    <FormattedMessage
                        id="ExpirationDateOffsetMetadata_Metadata_LessThanAMinute"
                        defaultMessage="Expires in less than a minute"
                        description="Text displaying from current time that the environment expires in less than a minute."
                    />
                );
            default:
                return <></>;
        }
    }, [offsetInteger, unit, values]);

    return environmentExpirationDisplay;
};

export const getShimmeredEnvironmentMetadata = (hasResourceGroup: boolean): ShimmeredMetadataItemViewModel[] => {
    const metadataItems = [
        { key: 'environment-type-shimmer', width: ShimmeredMetadataItemWidth.Small },
        { key: 'environment-owner-shimmer', width: ShimmeredMetadataItemWidth.Medium },
    ];

    if (hasResourceGroup) {
        metadataItems.push({ key: 'environment-resources-shimmer', width: ShimmeredMetadataItemWidth.Medium });
    }

    return metadataItems;
};

export const getEnvironmentStateMessage = (state: EnvironmentProvisioningState): StateMetadataViewModel | undefined => {
    switch (state) {
        case EnvironmentProvisioningState.Creating:
        case EnvironmentProvisioningState.Preparing:
            return {
                description: messages.creatingAriaLabel,
                icon: <New20Regular />,
                key: 'environment-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="EnvironmentStateMessage_CreatingMessage_Text"
                        defaultMessage="Creating..."
                        description="Message informing user that environment creation is in progress"
                    />
                ),
            };
        case EnvironmentProvisioningState.Deleting:
            return {
                description: messages.deletingAriaLabel,
                icon: <Eraser20Regular />,
                key: 'environment-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="EnvironmentStateMessage_DeletingState_Text"
                        defaultMessage="Deleting..."
                        description="Text informing user that environment is in the process of being deleted."
                    />
                ),
            };
        case EnvironmentProvisioningState.Deploying:
            return {
                description: messages.deployingAriaLabel,
                icon: <CloudSync20Regular />,
                key: 'environment-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="EnvironmentStateMessage_RedeployingState_Text"
                        defaultMessage="Redeploying..."
                        description="Text informing user that environment is in the process of redeploying."
                    />
                ),
            };

        case EnvironmentProvisioningState.Updating:
            return {
                description: messages.updatingAriaLabel,
                icon: <ArrowSyncCircle20Regular />,
                key: 'environment-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="EnvironmentStateMessage_UpdatingState_Text"
                        defaultMessage="Updating..."
                        description="Text informing user that environment is in the process of updating."
                    />
                ),
            };
        //TODO: Figure out state when running custom command and get the correct string from Brandon to display when a custom action is submitted
        //TODO: Unknown states are not expected, but showing another state is also not accurate.
        // For now the state text is empty if state is unknown.
        default:
            return undefined;
    }
};
